import React, { useState, useEffect, useMemo, useCallback } from "react";
import { withTranslation } from "react-i18next";
import StickySidebar from "../../../components/StickySidebar";
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel,
} from "react-accessible-accordion";
import "./../../../Scheduler.scss";
import SchedulerCalendar from "../../../components/SchedulerCalendar";
import OfficeFilter from "../../../components/SchedulerFilters/OfficeFilter";
import styles from "./EventsShiftsTab.module.scss";
import EventsShiftsWeekly from "./EventShiftsView/EventsShiftsWeekly";
import constants from "../../../../../../constants";
import moment from "moment";
import CustomDropdown from "components/Dropdown";
import EventsShiftsMonthly from "./EventShiftsView/EventsShiftsMonthly";
import EventsShiftsDaily from "./EventShiftsView/EventsShiftsDaily";
import EventsShiftsWeeklyStaffToolbar from "./EventShiftsView/EventsShiftsWeeklyStaffToolbar";
import { useEventsShiftsWeeklyBulkSelect } from "./EventShiftsView/useEventsShiftsWeeklyBulkSelect";
import { useEventsWorkspacePublish } from "./EventShiftsView/useEventsWorkspacePublish";
import { useEventsWorkspaceDelete } from "./EventShiftsView/useEventsWorkspaceDelete";
import { useEventsWorkspaceClear } from "./EventShiftsView/useEventsWorkspaceClear";
import { useEventsWorkspaceCopy } from "./EventShiftsView/useEventsWorkspaceCopy";
import { useEventsWorkspaceSaveTemplate } from "./EventShiftsView/useEventsWorkspaceSaveTemplate";
import { useEventsWorkspaceApplyTemplate } from "./EventShiftsView/useEventsWorkspaceApplyTemplate";
import PublishSchedulerEventsConfirmationModal from "./Modals/PublishSchedulerEventsConfirmationModal";
import DeleteSchedulerEventsConfirmationModal from "./Modals/DeleteSchedulerEventsConfirmationModal";
import ClearSchedulerPeriodConfirmationModal from "./Modals/ClearSchedulerPeriodConfirmationModal";
import CopySchedulerPeriodConfirmationModal from "./Modals/CopySchedulerPeriodConfirmationModal";
import SaveSchedulerTemplateModal from "./Modals/SaveSchedulerTemplateModal";
import AddFromSavedTemplateModal from "./Modals/AddFromSavedTemplateModal";
import {
  EVENT_WEEKLY_LAYOUT,
  EVENT_WEEKLY_SORT,
  resolveEventWeeklySort,
} from "./EventShiftsView/eventWeeklyLayout.constants";
import { cacheSideBarActive, getStorage, setStorage } from "utils";
import AgendaFilter from "accountOwner/pages/Scheduler/components/SchedulerFilters/AgendaFilter";
import { Link } from "react-router-dom";
import ExpandableSearchBar from "accountOwner/pages/AddEvent/SchedulingAssistant/components/ExpandableSearchBar";
import EmployeesFilter from "accountOwner/pages/Scheduler/components/SchedulerFilters/EmployeesFilter";
import { useLocation, useHistory } from "react-router-dom";
import { useGetHolidays } from "repositories/utility-repository";
import { useAccountOwnerCommon } from "lib/Context/AccountOwner";
import Loader from "components/Loader";
export const showTime = "showTime";

const EVENT_SHIFT_VIEW = {
  WEEKLY_LIST: "1",
  MONTHLY: "2",
  DAILY: "3",
  WEEKLY: "4",
};

const WEEK_BASED_VIEWS = new Set([EVENT_SHIFT_VIEW.WEEKLY]);

const resolveEventShiftView = (cachedViewId) => {
  if (cachedViewId === EVENT_SHIFT_VIEW.WEEKLY_LIST) {
    sessionStorage.setItem(
      constants.EVENT_TYPE_SESSION_KEY,
      EVENT_SHIFT_VIEW.WEEKLY,
    );
    return EVENT_SHIFT_VIEW.WEEKLY;
  }

  const validViewIds = new Set([
    EVENT_SHIFT_VIEW.DAILY,
    EVENT_SHIFT_VIEW.WEEKLY,
    EVENT_SHIFT_VIEW.MONTHLY,
  ]);

  return validViewIds.has(cachedViewId) ? cachedViewId : EVENT_SHIFT_VIEW.DAILY;
};

/**
 * Builds the date label shown between navigation arrows.
 */
const getViewDateLabel = (viewId, date) => {
  if (viewId === EVENT_SHIFT_VIEW.DAILY) {
    return moment(date).format("ddd, MMM DD, YYYY");
  }
  if (viewId === EVENT_SHIFT_VIEW.MONTHLY) {
    return moment(date).format("MMM YYYY");
  }
  if (WEEK_BASED_VIEWS.has(viewId)) {
    return `${moment(date).startOf("week").format("ddd, MMM DD")} - ${moment(
      date,
    )
      .endOf("week")
      .format("ddd, MMM DD")} `;
  }
  return "";
};

/**
 * Resolves the next active date from the selected view and navigation direction.
 */
const getNavigatedDate = (viewId, date, direction) => {
  const isPrevious = direction === constants.arrow.PREVIOUS;
  if (viewId === EVENT_SHIFT_VIEW.DAILY) {
    return new Date(
      isPrevious ? moment(date).subtract(1, "day") : moment(date).add(1, "day"),
    );
  }
  if (WEEK_BASED_VIEWS.has(viewId)) {
    return new Date(
      isPrevious
        ? moment(date).subtract(1, "week").startOf("week")
        : moment(date).add(1, "week").startOf("week"),
    );
  }
  return new Date(
    isPrevious
      ? moment(date).subtract(1, "month").startOf("month")
      : moment(date).add(1, "month").startOf("month"),
  );
};

/**
 * Removes view-incompatible display settings (e.g. monthly-only, staff-name weekly).
 */
const sanitizeViewSettingByView = (
  viewId,
  savedSettingFilter,
  weeklyLayoutOption,
) => {
  if (!savedSettingFilter?.length) {
    return [];
  }

  let result = [...savedSettingFilter];
  const monthlyOnlyFilterTypes = ["requestTojoin"];
  const staffNameWeeklyExcludedTypes = ["assignedEmployees"];

  if (viewId !== EVENT_SHIFT_VIEW.MONTHLY) {
    result = result.filter((type) => !monthlyOnlyFilterTypes.includes(type));
  }

  if (
    viewId === EVENT_SHIFT_VIEW.WEEKLY &&
    weeklyLayoutOption === EVENT_WEEKLY_LAYOUT.STAFF_NAME
  ) {
    result = result.filter(
      (type) => !staffNameWeeklyExcludedTypes.includes(type),
    );
  }

  return result;
};

function DateNavigation({
  eventShiftsViewSelected,
  currentDate,
  onNavigate,
  canHideWhenNoView = false,
}) {
  if (canHideWhenNoView && !eventShiftsViewSelected) {
    return null;
  }

  return (
    <div className="monthly-calendar-arrows">
      <div
        className="arrow-img"
        onClick={() => onNavigate(constants.arrow.PREVIOUS)}
      >
        <img
          src={require("assets/images/monthly-arrow-left.svg").default}
          alt="icon"
        />
      </div>

      <div className="monthly-calendar-text">
        {getViewDateLabel(eventShiftsViewSelected, currentDate)}
      </div>

      <div
        className="arrow-img"
        onClick={() => onNavigate(constants.arrow.NEXT)}
      >
        <img
          src={require("assets/images/monthly-arrow-right.svg").default}
          alt="icon"
        />
      </div>
    </div>
  );
}

function EventsShiftsTab({ t }) {
  const viewSettingFilterBase = useMemo(
    () => [
      { title: t("title"), type: "title" },
      { title: t("waitingList.clinicName"), type: "officeName" },
      { title: t("dateAndTime"), type: "timeString" },
      { title: t("assignedEmployees"), type: "assignedEmployees" },
      { title: t("requestTojoin"), type: "requestTojoin" },
    ],
    [t],
  );
  const enterpriseCache = getStorage(
    constants.localStorageKeys.agenda.eventWorkspaceEnterprise,
  );
  const eventShiftCache = getStorage(
    constants.localStorageKeys.agenda.eventShift,
  );
  const draftPublishCache = getStorage(
    constants.localStorageKeys.agenda.draftPublish,
  );
  const isFromOwnerOfficeModule = getStorage("isFromOwnerOfficeModule");

  const {
    isOwner,
    isStaff,
    effectiveOwnerId: selectedOwnerId,
    commonOwnerData: ownerData,
    dataAccessValues,
    effectiveOwnerIdString,
    profile,
  } = useAccountOwnerCommon();

  const { search } = useLocation();
  const history = useHistory();

  let cacheEventOffice = getStorage(constants.EVENT_OFFICE_FILTER)
    ? getStorage(constants.EVENT_OFFICE_FILTER)
    : [];

  const [currentDate, onDateChange] = useState(() => {
    const searchValue = new URLSearchParams(search).get(showTime);
    if (searchValue?.includes("NaN") || !search) {
      const cached = sessionStorage.getItem(constants.EVENT_DATE_SESSION_KEY);
      if (cached) {
        return new Date(cached);
      }
      return new Date();
    }
    return new Date(+searchValue);
  });
  const { data } = useGetHolidays(
    {
      year: currentDate.getFullYear(),
      month: currentDate.getMonth() + 1,
      OwnerId: selectedOwnerId,
    },
    {
      enabled: !!selectedOwnerId,
    },
  );
  const holidaysObject = useMemo(() => {
    if (data?.length) {
      return data.reduce((prev, curr) => {
        prev[moment(curr.date).format("DD-MM-YYYY")] = curr.title;
        return prev;
      }, {});
    }
    return {};
  }, [data]);

  const [selectedOfficeFilter, setOfficeFilter] = useState(cacheEventOffice);
  const [selectedEmpFilter, setSelectedEmpFilter] = useState(
    getStorage(constants.localStorageKeys.agenda.eventEmployeeFilter) || [],
  );
  const [appliedEmpFilter, setAppliedEmpFilter] = useState(
    getStorage(constants.localStorageKeys.agenda.eventEmployeeFilter) || [],
  );
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [resetSearchTrigger, setResetSearchTrigger] = useState(0);
  const [appliedOfficeFilter, setAppliedOfficeFilter] =
    useState(cacheEventOffice);
  const [upDateList, setupDateList] = useState(true);
  const [totalEvents, settotalEvents] = useState(0);
  const [searchValue, setSearchValue] = useState("");
  const [viewSettig, setViewSetting] = useState([]);
  const [settingFilter, setSettingFilter] = useState([]);
  const [isFocused, setIsFocused] = useState(false);
  const [employeeList, setEmployeeList] = useState([]);

  const [eventShiftFilter, setEventShiftFilter] = useState(
    eventShiftCache || [],
  );
  const [publishedDraftFilter, setPublishedDraftFilter] = useState(
    draftPublishCache || [],
  );
  const [appliedPublishedDraftFilter, setAppliedPublishedDraftFilter] =
    useState(draftPublishCache || []);
  const [appliedEventShiftFilter, setAppliedEventShiftFilter] = useState(
    eventShiftCache || [],
  );
  const [showEnterpriseEvents, setShowEnterpriseEvents] = useState(
    enterpriseCache || false,
  );

  const [isEnterpriseFilterActive, setIsEnterpriseFilterActive] = useState(
    enterpriseCache || false,
  );
  const userIdsSelected = useMemo(() => {
    return employeeList.reduce(
      (prev, curr) => {
        const value = appliedEmpFilter.includes(curr.id);
        const id = curr.userId ?? curr.id;
        if (value && !prev.keyPair.has(id)) {
          prev.returnedArray.push(id);
          prev.keyPair.set(id, id);
        }
        return prev;
      },
      {
        returnedArray: [],
        keyPair: new Map(),
      },
    ).returnedArray;
  }, [appliedEmpFilter, employeeList]);

  const preExpanded = useMemo(() => {
    const values = getStorage(
      constants.accordion.cache.schedulerOfficeEventAccordion,
    );
    if (values) {
      if (Array.isArray(values)) {
        return values;
      }
    }
    return ["a", "b"];
  }, []);

  const eventShiftsView = [
    { id: "3", name: "Daily" },
    { id: "4", name: "Weekly" },
    { id: "2", name: "Monthly" },
  ];
  const [eventShiftsViewSelected, seteventShiftsViewSelected] = useState(() =>
    resolveEventShiftView(
      sessionStorage.getItem(constants.EVENT_TYPE_SESSION_KEY),
    ),
  );

  const weeklyLayoutOptions = useMemo(
    () => [
      {
        id: EVENT_WEEKLY_LAYOUT.WEEKLY_CALENDAR,
        name: t("scheduler.viewWeekly"),
      },
      {
        id: EVENT_WEEKLY_LAYOUT.STAFF_NAME,
        name: t("scheduler.viewByStaffName"),
      },
    ],
    [t],
  );

  const [weeklyLayoutOption, setWeeklyLayoutOption] = useState(() => {
    const cached = sessionStorage.getItem(
      constants.EVENT_WEEKLY_LAYOUT_SESSION_KEY,
    );
    if (
      cached === EVENT_WEEKLY_LAYOUT.STAFF_NAME ||
      cached === EVENT_WEEKLY_LAYOUT.WEEKLY_CALENDAR
    ) {
      return cached;
    }
    return EVENT_WEEKLY_LAYOUT.STAFF_NAME;
  });

  const weeklySortOptions = useMemo(
    () => [
      {
        id: EVENT_WEEKLY_SORT.DEFAULT,
        name: t("scheduler.sortByDefault"),
      },
      {
        id: EVENT_WEEKLY_SORT.STAFF_NAME_ASC,
        name: t("scheduler.sortByStaffNameAsc"),
      },
      {
        id: EVENT_WEEKLY_SORT.STAFF_NAME_DESC,
        name: t("scheduler.sortByStaffNameDesc"),
      },
      {
        id: EVENT_WEEKLY_SORT.EMPLOYMENT_LATEST,
        name: t("scheduler.sortByEmploymentDateLatest"),
      },
      {
        id: EVENT_WEEKLY_SORT.EMPLOYMENT_OLDEST,
        name: t("scheduler.sortByEmploymentDateOldest"),
      },
    ],
    [t],
  );

  const [weeklySortOption, setWeeklySortOption] = useState(() => {
    const localValue = localStorage.getItem(
      constants.EVENT_WEEKLY_SORT_SESSION_KEY,
    );
    const sessionValue = sessionStorage.getItem(
      constants.EVENT_WEEKLY_SORT_SESSION_KEY,
    );
    const resolved = resolveEventWeeklySort(localValue || sessionValue);
    localStorage.setItem(constants.EVENT_WEEKLY_SORT_SESSION_KEY, resolved);
    if (sessionValue) {
      sessionStorage.removeItem(constants.EVENT_WEEKLY_SORT_SESSION_KEY);
    }
    return resolved;
  });

  const handleWeeklySortChange = useCallback((sortId) => {
    const resolved = resolveEventWeeklySort(sortId);
    setWeeklySortOption(resolved);
    localStorage.setItem(constants.EVENT_WEEKLY_SORT_SESSION_KEY, resolved);
  }, []);

  const refreshEventList = useCallback(() => {
    setupDateList((prev) => !prev);
  }, []);

  const {
    bulkSelectMode,
    selectedEvents,
    hasSelection,
    selectAllChecked,
    isBulkActionLoading,
    handleBulkSelectModeChange,
    handleSelectAllChange,
    handleEventSelectionToggle,
    handleSelectableEventsUpdate,
    clearSelection,
    showComingSoon,
  } = useEventsShiftsWeeklyBulkSelect({
    t,
  });

  const clearBulkSelection = useCallback(() => {
    handleBulkSelectModeChange(false);
  }, [handleBulkSelectModeChange]);

  const isStaffScopedDelete =
    eventShiftsViewSelected === EVENT_SHIFT_VIEW.WEEKLY &&
    weeklyLayoutOption === EVENT_WEEKLY_LAYOUT.STAFF_NAME;

  const {
    handleVisibleDraftEventsUpdate,
    canPublishGlobal,
    canPublishSelected,
    isPublishing: isWorkspacePublishing,
    confirmModal: publishConfirmModal,
    openGlobalPublishConfirm,
    openPublishSelectedConfirm,
    closeConfirmModal: closePublishConfirmModal,
    confirmPublish,
  } = useEventsWorkspacePublish({
    t,
    calendarViewId: eventShiftsViewSelected,
    currentDate,
    selectedEvents,
    onRefresh: refreshEventList,
    onClearSelection: clearBulkSelection,
  });

  const {
    canDeleteSelected,
    isDeleting: isWorkspaceDeleting,
    confirmModal: deleteConfirmModal,
    openDeleteSelectedConfirm,
    closeConfirmModal: closeDeleteConfirmModal,
    confirmDelete,
  } = useEventsWorkspaceDelete({
    t,
    selectedEvents,
    isForAllStaff: !isStaffScopedDelete,
    onRefresh: refreshEventList,
    onClearSelection: clearSelection,
  });

  const {
    visibleClearableItems,
    canClearPeriod,
    isClearing: isWorkspaceClearing,
    confirmModal: clearConfirmModal,
    handleVisibleClearableItemsUpdate,
    openClearPeriodConfirm,
    closeConfirmModal: closeClearConfirmModal,
    confirmClear,
  } = useEventsWorkspaceClear({
    t,
    calendarViewId: eventShiftsViewSelected,
    currentDate,
    ownerId: selectedOwnerId,
    eventShiftFilter: appliedEventShiftFilter,
    officeIds: appliedOfficeFilter,
    userIds: userIdsSelected,
    onRefresh: refreshEventList,
    onClearSelection: clearBulkSelection,
  });

  const {
    canCopyPrevious,
    isCopying: isWorkspaceCopying,
    isPreparing: isWorkspaceCopyPreparing,
    confirmModal: copyConfirmModal,
    openCopyPreviousConfirm,
    closeConfirmModal: closeCopyConfirmModal,
    confirmCopy,
  } = useEventsWorkspaceCopy({
    t,
    calendarViewId: eventShiftsViewSelected,
    currentDate,
    ownerId: selectedOwnerId,
    eventShiftFilter: appliedEventShiftFilter,
    publishedDraftFilter: appliedPublishedDraftFilter,
    isEnterpriseFilterActive,
    officeIds: appliedOfficeFilter,
    userIds: userIdsSelected,
    profileId: profile?.id,
    onRefresh: refreshEventList,
  });

  const {
    isModalOpen: isSaveTemplateModalOpen,
    isSaving: isWorkspaceSavingTemplate,
    canSaveAsTemplate,
    openSaveTemplateModal,
    closeModal: closeSaveTemplateModal,
    executeSave: confirmSaveTemplate,
  } = useEventsWorkspaceSaveTemplate({
    t,
    calendarViewId: eventShiftsViewSelected,
    currentDate,
    ownerId: selectedOwnerId,
    visibleItems: visibleClearableItems,
    eventShiftFilter: appliedEventShiftFilter,
    officeIds: appliedOfficeFilter,
    userIds: userIdsSelected,
  });

  const {
    isModalOpen: isApplyTemplateModalOpen,
    isApplying: isWorkspaceApplyingTemplate,
    canApplyTemplate,
    openApplyTemplateModal,
    closeModal: closeApplyTemplateModal,
    executeApply: confirmApplyTemplate,
  } = useEventsWorkspaceApplyTemplate({
    t,
    calendarViewId: eventShiftsViewSelected,
    currentDate,
    ownerId: selectedOwnerId,
    onRefresh: refreshEventList,
  });

  const handleWeeklyLayoutChange = useCallback(
    (layoutId) => {
      setWeeklyLayoutOption(layoutId);
      sessionStorage.setItem(
        constants.EVENT_WEEKLY_LAYOUT_SESSION_KEY,
        layoutId,
      );
      if (layoutId === EVENT_WEEKLY_LAYOUT.STAFF_NAME) {
        setViewSetting((prevViewSetting) => {
          const sanitizedFilter = sanitizeViewSettingByView(
            EVENT_SHIFT_VIEW.WEEKLY,
            prevViewSetting,
            layoutId,
          );
          if (sanitizedFilter.length !== prevViewSetting.length) {
            setSettingFilter(sanitizedFilter);
            setStorage(constants.EVENT_DISPLAY_SETTINGS, sanitizedFilter);
            return sanitizedFilter;
          }
          return prevViewSetting;
        });
      }
      if (eventShiftsViewSelected !== EVENT_SHIFT_VIEW.WEEKLY) {
        sessionStorage.setItem(
          constants.EVENT_TYPE_SESSION_KEY,
          EVENT_SHIFT_VIEW.WEEKLY,
        );
        seteventShiftsViewSelected(EVENT_SHIFT_VIEW.WEEKLY);
        setTimeout(() => setupDateList((prev) => !prev), 100);
      }
    },
    [eventShiftsViewSelected],
  );

  const ViewSettingFilter = useMemo(() => {
    if (eventShiftsViewSelected === "4" || eventShiftsViewSelected === "3") {
      const filters = [
        { title: t("Tag"), type: "tag" },
        { title: t("dateAndTime"), type: "timeString" },
        { title: t("waitingList.clinicName"), type: "officeName" },
        { title: t("title"), type: "title" },
        { title: t("assignedEmployees"), type: "assignedEmployees" },
      ];

      if (
        eventShiftsViewSelected === EVENT_SHIFT_VIEW.WEEKLY &&
        weeklyLayoutOption === EVENT_WEEKLY_LAYOUT.STAFF_NAME
      ) {
        return filters.filter((filter) => filter.type !== "assignedEmployees");
      }

      return filters;
    }
    return [...viewSettingFilterBase, { title: t("Tag"), type: "tag" }];
  }, [eventShiftsViewSelected, t, viewSettingFilterBase, weeklyLayoutOption]);

  const [isSidebarActive, setSidebarActive] = useState(
    cacheSideBarActive(constants.sidebar.cache.schedulerIsSidebarActive),
  );

  useEffect(() => {
    if (isFromOwnerOfficeModule?.officeId) {
      setOfficeFilter([isFromOwnerOfficeModule?.officeId]);
      setAppliedOfficeFilter([isFromOwnerOfficeModule?.officeId]);
    }
  }, []);

  useEffect(() => {
    setSearchValue("");
    // eslint-disable-next-line
  }, [selectedOwnerId]);

  useEffect(() => {
    setSearchValue("");
    sessionStorage.setItem(constants.EVENT_DATE_SESSION_KEY, currentDate);
  }, [currentDate]);

  const scrollToElement = () => {
    const element = document.getElementById("targetElementId");
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "start",
      });
    }
  };

  const applyFilter = () => {
    scrollToElement();
    setSearchValue("");
    setupDateList((e) => !e);
    setAppliedOfficeFilter(selectedOfficeFilter);
    setAppliedEmpFilter(selectedEmpFilter);
    setStorage(constants.EVENT_OFFICE_FILTER, selectedOfficeFilter);
    setStorage(
      constants.localStorageKeys.agenda.eventWorkspaceEnterprise,
      showEnterpriseEvents,
    );
    setStorage(
      constants.localStorageKeys.agenda.eventEmployeeFilter,
      selectedEmpFilter,
    ); // persist employee filter
    if (viewSettig?.length > 0) {
      setSettingFilter(viewSettig);
      setStorage(constants.EVENT_DISPLAY_SETTINGS, viewSettig);
    } else {
      setSettingFilter([]);
      setStorage(constants.EVENT_DISPLAY_SETTINGS, viewSettig);
    }
    setAppliedEventShiftFilter(eventShiftFilter);
    setStorage(constants.localStorageKeys.agenda.eventShift, eventShiftFilter);
    setAppliedPublishedDraftFilter(publishedDraftFilter);
    setStorage(
      constants.localStorageKeys.agenda.draftPublish,
      publishedDraftFilter,
    );
    setIsEnterpriseFilterActive(showEnterpriseEvents);
  };

  const resetFilter = () => {
    setSearchValue("");
    setOfficeFilter([]);
    setAppliedOfficeFilter([]);
    setSelectedEmpFilter([]);
    setAppliedEmpFilter([]);
    setSelectedRoles([]);
    setResetSearchTrigger((prev) => prev + 1);
    setStorage(constants.localStorageKeys.agenda.eventEmployeeFilter, []); // clear employee filter
    setupDateList((e) => !e);
    setViewSetting([]);
    setSettingFilter([]);
    setEventShiftFilter([]);
    setAppliedEventShiftFilter([]);
    setPublishedDraftFilter([]);
    setAppliedPublishedDraftFilter([]);
    setShowEnterpriseEvents(false);
    setIsEnterpriseFilterActive(false);
    setStorage(constants.EVENT_OFFICE_FILTER, []);
    setStorage(constants.EVENT_DISPLAY_SETTINGS, []);
    setStorage(
      constants.localStorageKeys.agenda.eventWorkspaceEnterprise,
      false,
    );
    setStorage(constants.localStorageKeys.agenda.eventShift, []);
    setStorage(constants.localStorageKeys.agenda.draftPublish, []);
  };

  useEffect(() => {
    // Centralizes side-effects that must run together when view changes.
    setSearchValue("");
    sessionStorage.setItem(
      constants.EVENT_TYPE_SESSION_KEY,
      eventShiftsViewSelected,
    );

    // Force refresh of data
    setTimeout(() => {
      setupDateList((prev) => !prev);
    }, 100);

    const SettingFilter = getStorage(constants.EVENT_DISPLAY_SETTINGS);
    const savedSettingFilter = SettingFilter?.length > 0 ? SettingFilter : [];
    const settingFilter = sanitizeViewSettingByView(
      eventShiftsViewSelected,
      savedSettingFilter,
      weeklyLayoutOption,
    );
    if (savedSettingFilter.length !== settingFilter.length) {
      setStorage(constants.EVENT_DISPLAY_SETTINGS, settingFilter);
    }
    setViewSetting(settingFilter);
    setSettingFilter(settingFilter);
  }, [eventShiftsViewSelected, weeklyLayoutOption]);

  const handleViewChange = (value) => {
    // Clear search
    setSearchValue("");

    sessionStorage.setItem(constants.EVENT_TYPE_SESSION_KEY, value);

    seteventShiftsViewSelected(value);

    setTimeout(() => {
      setupDateList((prev) => !prev);
    }, 100);
  };

  const handleSidebarToggle = () => {
    setSidebarActive(!isSidebarActive);
    localStorage.setItem(
      constants.sidebar.cache.schedulerIsSidebarActive,
      !isSidebarActive,
    );
  };

  const dateChange = (type) => {
    onDateChange(getNavigatedDate(eventShiftsViewSelected, currentDate, type));
  };

  const onActiveStartDateChange = (value) => {
    if (
      constants.calanderActions.includes(value?.action) ||
      (value?.action === constants.calanderActionKey.drillDown &&
        (value?.view === "month" || value?.view === "year"))
    ) {
      value?.activeStartDate && onDateChange(value.activeStartDate);
    }
  };

  const isFilterApply =
    appliedOfficeFilter?.length ||
    appliedEmpFilter?.length ||
    viewSettig?.length ||
    isEnterpriseFilterActive ||
    appliedEventShiftFilter?.length ||
    appliedPublishedDraftFilter?.length;

  useEffect(() => {
    if (
      selectedOwnerId &&
      (eventShiftsViewSelected === "2" || eventShiftsViewSelected === "3")
    ) {
      setTimeout(() => {
        setupDateList((prev) => !prev);
      }, 200);
    }
  }, []);

  const canAddShift = useMemo(
    () => isOwner || (isStaff && dataAccessValues?.isAdmin),
    [isOwner, isStaff, dataAccessValues],
  );
  const filters = [
    {
      accordionKey: "d",
      heading: t("Event/Shift"),
      options: [
        {
          keys: t("Event"),
          value: "event",
        },
        {
          keys: t("Shift"),
          value: "shift",
        },
        {
          keys: t("scheduler.agenda.shiftCreatedByOthers"),
          value: "shift-others",
        },
      ],
      value: eventShiftFilter,
      setter: setEventShiftFilter,
    },
    {
      accordionKey: "e",
      heading: t("eventStatus"),
      options: [
        {
          keys: t("drafted"),
          value: "draft",
        },
        {
          keys: t("published"),
          value: "published",
        },
      ],
      value: publishedDraftFilter,
      setter: setPublishedDraftFilter,
    },
  ];

  const selectedViewComponent = useMemo(
    () =>
      ({
        [EVENT_SHIFT_VIEW.DAILY]: EventsShiftsDaily,
        [EVENT_SHIFT_VIEW.WEEKLY]: EventsShiftsWeekly,
        [EVENT_SHIFT_VIEW.MONTHLY]: EventsShiftsMonthly,
      })[eventShiftsViewSelected],
    [eventShiftsViewSelected],
  );

  const sharedViewProps = useMemo(
    () => ({
      selectedOfficeFilter: appliedOfficeFilter,
      selectedEmpFilter: userIdsSelected,
      upDateListonApply: upDateList,
      searchValue,
      isSidebarActive,
      handleSidebarToggle,
      calendarView: eventShiftsViewSelected,
      currentDate,
      setcurrentDate: onDateChange,
      settotalEvents,
      canAddShift,
      viewSettingFilter: settingFilter,
      eventShiftFilter: appliedEventShiftFilter,
      isEnterpriseFilterActive,
      appliedPublishedDraftFilter,
      holidaysObject,
      toggleRefreshEventList: refreshEventList,
      weeklyLayoutOption,
      weeklyLayoutOptions,
      onWeeklyLayoutChange: handleWeeklyLayoutChange,
      sortStaffBy: weeklySortOption,
      bulkSelectMode,
      selectedEvents,
      hasSelection,
      isBulkActionLoading,
      handleBulkSelectModeChange,
      handleSelectAllChange,
      handleEventSelectionToggle,
      handleSelectableEventsUpdate,
      onVisibleDraftEventsUpdate: handleVisibleDraftEventsUpdate,
      onVisibleClearableItemsUpdate: handleVisibleClearableItemsUpdate,
      showComingSoon,
    }),
    [
      appliedOfficeFilter,
      userIdsSelected,
      upDateList,
      searchValue,
      isSidebarActive,
      handleSidebarToggle,
      eventShiftsViewSelected,
      currentDate,
      onDateChange,
      settotalEvents,
      canAddShift,
      settingFilter,
      appliedEventShiftFilter,
      isEnterpriseFilterActive,
      appliedPublishedDraftFilter,
      holidaysObject,
      refreshEventList,
      weeklyLayoutOption,
      weeklyLayoutOptions,
      handleWeeklyLayoutChange,
      weeklySortOption,
      bulkSelectMode,
      selectedEvents,
      hasSelection,
      isBulkActionLoading,
      handleBulkSelectModeChange,
      handleSelectAllChange,
      handleEventSelectionToggle,
      handleSelectableEventsUpdate,
      handleVisibleDraftEventsUpdate,
      handleVisibleClearableItemsUpdate,
      showComingSoon,
    ],
  );
  const SelectedViewComponent = selectedViewComponent;

  const handleToolbarCopyPreviousWeek = useCallback(
    () => openCopyPreviousConfirm(),
    [openCopyPreviousConfirm],
  );
  const handleToolbarClearWeek = useCallback(
    () => openClearPeriodConfirm(),
    [openClearPeriodConfirm],
  );
  const handleToolbarSaveWeekAsTemplate = useCallback(
    () => openSaveTemplateModal(),
    [openSaveTemplateModal],
  );
  const handleToolbarAddFromSavedTemplate = useCallback(
    () => openApplyTemplateModal(),
    [openApplyTemplateModal],
  );
  const handleToolbarPublishSelected = useCallback(
    () => openPublishSelectedConfirm(),
    [openPublishSelectedConfirm],
  );
  const handleToolbarUnpublishSelected = useCallback(
    () => showComingSoon(t("scheduler.unpublishSelected")),
    [showComingSoon, t],
  );
  const handleToolbarDeleteSelected = useCallback(
    () => openDeleteSelectedConfirm(),
    [openDeleteSelectedConfirm],
  );
  const handleToolbarPublishWeek = useCallback(
    () => openGlobalPublishConfirm(),
    [openGlobalPublishConfirm],
  );

  const isWorkspaceActionLoading =
    isWorkspacePublishing ||
    isWorkspaceDeleting ||
    isWorkspaceClearing ||
    isWorkspaceCopying ||
    isWorkspaceCopyPreparing ||
    isWorkspaceSavingTemplate ||
    isWorkspaceApplyingTemplate;

  return (
    <>
      {isWorkspaceActionLoading && <Loader />}
      <PublishSchedulerEventsConfirmationModal
        isOpen={Boolean(publishConfirmModal)}
        isLoading={isWorkspacePublishing}
        calendarViewId={eventShiftsViewSelected}
        currentDate={currentDate}
        shiftCount={publishConfirmModal?.shiftCount ?? 0}
        eventCount={publishConfirmModal?.eventCount ?? 0}
        onConfirm={confirmPublish}
        onClose={closePublishConfirmModal}
      />
      <DeleteSchedulerEventsConfirmationModal
        isOpen={Boolean(deleteConfirmModal)}
        isLoading={isWorkspaceDeleting}
        calendarViewId={eventShiftsViewSelected}
        currentDate={currentDate}
        shiftCount={deleteConfirmModal?.shiftCount ?? 0}
        eventCount={deleteConfirmModal?.eventCount ?? 0}
        onConfirm={confirmDelete}
        onClose={closeDeleteConfirmModal}
      />
      <ClearSchedulerPeriodConfirmationModal
        isOpen={Boolean(clearConfirmModal)}
        isLoading={isWorkspaceClearing}
        calendarViewId={eventShiftsViewSelected}
        currentDate={currentDate}
        shiftCount={clearConfirmModal?.shiftCount ?? 0}
        eventCount={clearConfirmModal?.eventCount ?? 0}
        onConfirm={confirmClear}
        onClose={closeClearConfirmModal}
      />
      <CopySchedulerPeriodConfirmationModal
        isOpen={Boolean(copyConfirmModal)}
        isLoading={isWorkspaceCopying}
        calendarViewId={eventShiftsViewSelected}
        shiftCount={copyConfirmModal?.shiftCount ?? 0}
        eventCount={copyConfirmModal?.eventCount ?? 0}
        onConfirm={confirmCopy}
        onClose={closeCopyConfirmModal}
      />
      <SaveSchedulerTemplateModal
        isOpen={isSaveTemplateModalOpen}
        isLoading={isWorkspaceSavingTemplate}
        calendarViewId={eventShiftsViewSelected}
        currentDate={currentDate}
        onConfirm={confirmSaveTemplate}
        onClose={closeSaveTemplateModal}
      />
      <AddFromSavedTemplateModal
        isOpen={isApplyTemplateModalOpen}
        isLoading={isWorkspaceApplyingTemplate}
        calendarViewId={eventShiftsViewSelected}
        currentDate={currentDate}
        ownerId={selectedOwnerId}
        onConfirm={confirmApplyTemplate}
        onClose={closeApplyTemplateModal}
      />
      <Link
        to={{
          pathname: constants.routes.scheduler.addEvent,
          state: {
            date: moment().format("YYYY-MM-DD"),
          },
          search: effectiveOwnerIdString ? `?${effectiveOwnerIdString}` : "",
        }}
        className={styles["add-event-btn"] + " staff-calendar-select-box "}
      >
        <button className={"button button-round  button-shadow"}>
          {t(
            canAddShift
              ? "accountOwner.newEventShift"
              : "accountOwner.newEvent",
          )}
        </button>
      </Link>
      <div className="scheduler-tabs-main-wrapper">
        <StickySidebar
          isSidebarActive={isSidebarActive}
          handleSidebarToggle={handleSidebarToggle}
          resetFilter={resetFilter}
        >
          <SchedulerCalendar
            value={currentDate}
            onChange={onDateChange}
            onActiveStartDateChange={onActiveStartDateChange}
            activeStartDate={currentDate}
            showTodayButton={true}
            onTodayClick={() => {
              const today = new Date();
              onDateChange(today);
              const pathname = history.location.pathname;
              const queryParams = new URLSearchParams(history.location.search);
              queryParams.set(showTime, String(today.getTime()));
              history.replace({ pathname, search: queryParams.toString() });
            }}
          />
          <div className="mb-4">
            <span className={styles.notes_filter}>
              {t("notes.filters")}
              {!!isFilterApply && <span className={styles.red_dot}></span>}
            </span>
            <ul className={["filter-list"]}>
              <li>
                <div className="ch-checkbox">
                  <label>
                    <input
                      type="checkbox"
                      checked={showEnterpriseEvents}
                      onChange={(e) =>
                        setShowEnterpriseEvents(e.target.checked)
                      }
                    />{" "}
                    <span> {t("showEnterpriseEvents")}</span>
                  </label>
                </div>
              </li>
            </ul>
          </div>
          <Accordion
            preExpanded={preExpanded}
            className="filter-accordion"
            allowZeroExpanded
            allowMultipleExpanded
            onChange={(e) => {
              setStorage(
                constants.accordion.cache.schedulerOfficeEventAccordion,
                e,
              );
            }}
          >
            <AccordionItem uuid="a">
              <AccordionItemHeading>
                <AccordionItemButton>{t("offices")}</AccordionItemButton>
              </AccordionItemHeading>
              <AccordionItemPanel>
                <OfficeFilter
                  selectedOffice={selectedOfficeFilter}
                  setOfficeFilter={setOfficeFilter}
                  selectedOwnerId={selectedOwnerId}
                  includeOwnerOffices
                />
              </AccordionItemPanel>
            </AccordionItem>
            <AccordionItem uuid="b">
              <AccordionItemHeading>
                <AccordionItemButton>
                  {t("accountOwner.staff")}
                </AccordionItemButton>
              </AccordionItemHeading>
              <AccordionItemPanel>
                <EmployeesFilter
                  selectedEmpFilter={selectedEmpFilter}
                  setEmpFilter={setSelectedEmpFilter}
                  ownerId={selectedOwnerId}
                  ownerData={ownerData || {}}
                  selectedOfficeFilter={selectedOfficeFilter}
                  selectedRoles={selectedRoles}
                  setSelectedRoles={setSelectedRoles}
                  setUsersList={setEmployeeList}
                  resetSearchTrigger={resetSearchTrigger}
                />
              </AccordionItemPanel>
            </AccordionItem>
            {filters.map(
              ({ accordionKey, heading, options, setter, value }) => (
                <AccordionItem uuid={accordionKey} key={accordionKey}>
                  <AccordionItemHeading>
                    <AccordionItemButton>{heading}</AccordionItemButton>
                  </AccordionItemHeading>
                  <AccordionItemPanel>
                    <ul className={["filter-list"]}>
                      {options.map(({ keys, value: optionValue }) => {
                        return (
                          <li key={optionValue}>
                            <div className="ch-checkbox">
                              <label>
                                <input
                                  type="checkbox"
                                  checked={value.includes(optionValue)}
                                  onChange={() => {
                                    setter((prev) => {
                                      const values = prev.includes(optionValue)
                                        ? prev.filter((v) => v !== optionValue)
                                        : [...prev, optionValue];
                                      return values;
                                    });
                                  }}
                                />{" "}
                                <span> {keys}</span>
                              </label>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </AccordionItemPanel>
                </AccordionItem>
              ),
            )}
            <AccordionItem uuid="c">
              <AccordionItemHeading>
                <AccordionItemButton>
                  {t("accountOwner.viewSetting")}
                </AccordionItemButton>
              </AccordionItemHeading>
              <AccordionItemPanel>
                <AgendaFilter
                  setAgendaTypes={setViewSetting}
                  agendaTypes={viewSettig}
                  FilterEventsData={ViewSettingFilter}
                />
              </AccordionItemPanel>
            </AccordionItem>
          </Accordion>

          <div className="filter-btn-box filter-btn-box--sticky-footer ">
            <button
              className={"button button-round button-shadow mr-3"}
              title={t("apply")}
              onClick={applyFilter}
            >
              {t("apply")}
            </button>
            <button
              className={
                "button button-round button-border button-dark reset-btn"
              }
              title={t("reset")}
              onClick={resetFilter}
            >
              {t("reset")}
            </button>
            <button
              className={
                "button button-round button-border button-dark cancel-btn"
              }
              title={t("close")}
              onClick={handleSidebarToggle}
            >
              {t("close")}
            </button>
          </div>
        </StickySidebar>
        <div className="tabs-right-col" id="targetElementId">
          <div className={styles["event-shift-header"]}>
            <div className={styles["search-form-calc"]}>
              <ExpandableSearchBar
                placeholder={t("accountOwner.searchForTagsEventsTitle")}
                searchValue={searchValue}
                setSearchValue={setSearchValue}
                isFocused={isFocused}
                setIsFocused={setIsFocused}
              />
            </div>
            <div className={`d-lg-flex d-none`}>
              <DateNavigation
                eventShiftsViewSelected={eventShiftsViewSelected}
                currentDate={currentDate}
                onNavigate={dateChange}
                canHideWhenNoView
              />
              <CustomDropdown
                options={eventShiftsView}
                selectedOption={eventShiftsViewSelected}
                selectOption={handleViewChange}
              />
            </div>
          </div>
          <div className={`d-lg-none d-flex  ${styles["event-shift-view"]}`}>
            <DateNavigation
              eventShiftsViewSelected={eventShiftsViewSelected}
              currentDate={currentDate}
              onNavigate={dateChange}
            />
            <CustomDropdown
              options={eventShiftsView}
              selectedOption={eventShiftsViewSelected}
              selectOption={handleViewChange}
            />
          </div>

          {searchValue?.trim() && totalEvents > 0 ? (
            <div className="d-flex align-items-center ">
              {" "}
              {totalEvents}
              {t("scheduler.eventFound")}{" "}
            </div>
          ) : (
            <div className="d-none align-items-center">
              {t("scheduler.noEventsFound")}
            </div>
          )}
          {selectedOwnerId && (
            <EventsShiftsWeeklyStaffToolbar
              calendarViewId={eventShiftsViewSelected}
              showViewOptions={
                eventShiftsViewSelected === EVENT_SHIFT_VIEW.WEEKLY
              }
              showSortBy={
                eventShiftsViewSelected === EVENT_SHIFT_VIEW.WEEKLY &&
                weeklyLayoutOption === EVENT_WEEKLY_LAYOUT.STAFF_NAME
              }
              weeklyLayoutOption={weeklyLayoutOption}
              onWeeklyLayoutChange={handleWeeklyLayoutChange}
              weeklyLayoutOptions={weeklyLayoutOptions}
              sortStaffBy={weeklySortOption}
              onSortStaffByChange={handleWeeklySortChange}
              sortStaffOptions={weeklySortOptions}
              bulkSelectMode={bulkSelectMode}
              onBulkSelectModeChange={handleBulkSelectModeChange}
              selectAllChecked={selectAllChecked}
              onSelectAllChange={handleSelectAllChange}
              hasSelection={hasSelection}
              canPublishGlobal={canPublishGlobal}
              canPublishSelected={canPublishSelected}
              canDeleteSelected={canDeleteSelected}
              canClearPeriod={canClearPeriod}
              canCopyPrevious={canCopyPrevious}
              canSaveAsTemplate={canSaveAsTemplate}
              canApplyTemplate={canApplyTemplate}
              isPublishing={isWorkspacePublishing}
              isDeleting={isWorkspaceDeleting}
              isClearing={isWorkspaceClearing}
              isCopying={isWorkspaceCopying || isWorkspaceCopyPreparing}
              isSavingTemplate={isWorkspaceSavingTemplate}
              isApplyingTemplate={isWorkspaceApplyingTemplate}
              onCopyPreviousWeek={handleToolbarCopyPreviousWeek}
              onClearWeek={handleToolbarClearWeek}
              onSaveWeekAsTemplate={handleToolbarSaveWeekAsTemplate}
              onAddFromSavedTemplate={handleToolbarAddFromSavedTemplate}
              onPublishSelected={handleToolbarPublishSelected}
              onUnpublishSelected={handleToolbarUnpublishSelected}
              onDeleteSelected={handleToolbarDeleteSelected}
              onPublishWeek={handleToolbarPublishWeek}
            />
          )}
          {selectedOwnerId && SelectedViewComponent && (
            <SelectedViewComponent {...sharedViewProps} />
          )}
        </div>
      </div>
    </>
  );
}

export default withTranslation()(EventsShiftsTab);
